import { useState } from 'react';
import { roles as Api, type PermissionGroup, type Role } from '@tvx/api-client';
import { useAsync } from '../lib/useAsync';
import { Shim } from '../components/Shimmer';
import { Modal } from '../components/Modal';
import { Field } from '../components/Field';

export function Roles() {
  const { data, error, loading, reload } = useAsync((signal) => Api.list(signal));
  const [editing, setEditing] = useState<Role | 'new' | null>(null);
  const roles = data?.roles ?? [];

  async function remove(role: Role) {
    if (role.members > 0) {
      alert(`${role.members} ${role.members === 1 ? 'person holds' : 'people hold'} this role. Move them first.`);
      return;
    }
    if (!confirm(`Delete the ${role.name} role?`)) return;
    try { await Api.remove(role.id); await reload(); }
    catch (e) { alert((e as Error).message); }
  }

  return (
    <div className="pg">
      <div className="rowbar">
        <div>
          <h1 style={{ fontSize: 29 }}>Roles</h1>
          <p style={{ fontSize: 13.5, color: 'var(--tx2)', marginTop: 7 }}>
            What each role may open. A screen nobody's role grants is refused by the server, not just hidden.
          </p>
        </div>
        <button className="b bg" type="button" onClick={() => setEditing('new')}>New role</button>
      </div>

      {error ? <div className="err">Could not load roles — {error.message}</div> : null}

      <div style={{ display: 'grid', gap: 14 }}>
        {loading && !data ? <Shim w="55%" h={16} /> : roles.map((r) => (
          <div key={r.id} className="crd" style={{ padding: '18px 22px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ fontFamily: 'var(--fd)', fontSize: 17, fontWeight: 600 }}>
                  {r.name}
                  {r.is_owner ? <span className="tg" style={{ marginLeft: 8, background: 'var(--goldsoft)',
                    color: 'var(--goldtx)' }}>owner</span> : null}
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--tx3)', marginTop: 3 }}>
                  {r.members} {r.members === 1 ? 'person' : 'people'} ·{' '}
                  {r.is_owner ? 'everything, always' : `${r.permissions.length} screens`}
                </div>
              </div>
              <button className="b bo" style={{ padding: '6px 12px', fontSize: 11.5 }}
                type="button" onClick={() => setEditing(r)}>{r.is_owner ? 'View' : 'Edit'}</button>
              {!r.is_owner ? (
                <button className="b bo" style={{ padding: '6px 12px', fontSize: 11.5 }}
                  type="button" onClick={() => void remove(r)}>Delete</button>
              ) : null}
            </div>
          </div>
        ))}
      </div>

      {editing ? (
        <RoleForm
          role={editing === 'new' ? null : editing}
          groups={data?.groups ?? []}
          ownerLocked={data?.owner_locked ?? []}
          onCancel={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await reload(); }}
        />
      ) : null}
    </div>
  );
}

function RoleForm({ role, groups, ownerLocked, onCancel, onSaved }: {
  role: Role | null;
  groups: PermissionGroup[];
  ownerLocked: string[];
  onCancel: () => void;
  onSaved: () => Promise<void>;
}) {
  const [name, setName] = useState(role?.name ?? '');
  const [permissions, setPermissions] = useState<Set<string>>(new Set(role?.permissions ?? []));
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  /* The owner role is what stops somebody locking everybody out of their own
     console, so it is shown and not edited. */
  const readOnly = !!role?.is_owner;

  const toggle = (key: string) => setPermissions((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  async function save() {
    if (!name.trim()) { setError('Give the role a name.'); return; }
    setError('');
    setStatus('Saving…');
    try {
      const body = { name, permissions: [...permissions] };
      if (role) await Api.update(role.id, body);
      else await Api.create(body);
      await onSaved();
    } catch (e) { setStatus(''); setError((e as Error).message); }
  }

  return (
    <Modal
      title={role ? `${readOnly ? 'Role' : 'Edit role'} · ${role.name}` : 'New role'}
      onClose={onCancel}
      footer={
        <>
          <span style={{ fontSize: 12, color: 'var(--tx3)' }}>{status}</span>
          <span className="mdl-spacer" />
          <button className="b bo" type="button" onClick={onCancel}>{readOnly ? 'Close' : 'Cancel'}</button>
          {!readOnly ? <button className="b bg" type="button" onClick={() => void save()}>Save role</button> : null}
        </>
      }
    >
      {error ? <div className="err">{error}</div> : null}
      {readOnly ? (
        <p className="hint" style={{ marginBottom: 14 }}>
          The owner role always holds every permission and cannot be narrowed — otherwise the last owner
          could remove their own way back in.
        </p>
      ) : null}

      <Field label="Name">
        <input className="inp" value={name} disabled={readOnly} autoFocus={!readOnly}
          onChange={(e) => setName(e.target.value)} />
      </Field>

      {groups.map((g) => (
        <div key={g.group} style={{ marginTop: 16 }}>
          <div style={{ fontSize: 10, letterSpacing: '.12em', textTransform: 'uppercase',
            color: 'var(--tx3)', marginBottom: 8 }}>{g.group}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: '6px 16px' }}>
            {g.items.map((item) => {
              const locked = readOnly || (role?.is_owner && ownerLocked.includes(item.key));
              return (
                <label key={item.key} style={{ display: 'flex', gap: 9, alignItems: 'center',
                  cursor: locked ? 'default' : 'pointer', fontSize: 13.5 }}>
                  <input type="checkbox" disabled={locked}
                    checked={readOnly ? true : permissions.has(item.key)}
                    onChange={() => toggle(item.key)} />
                  {item.label}
                </label>
              );
            })}
          </div>
        </div>
      ))}
    </Modal>
  );
}
